import React, {
  FC,
  useState,
  useCallback,
  useEffect,
  ChangeEvent,
  useMemo,
} from 'react';
import { observer } from 'mobx-react';
import {
  Checkbox,
  DialogActions,
  DialogContent,
  Divider,
  MenuItem,
  Select,
  TextField,
  TextFieldProps,
} from '@mui/material';
import styled from 'styled-components';
import Dialog from 'components/UI/Dialog';
import Button from 'components/UI/Button';
import Input from 'components/UI/Input';
import Grid from 'components/UI/Grid';
import Text from 'components/UI/Text';
import { theme } from 'components/UI';
import { useTranslation } from 'react-i18next';
import { useStores } from 'stores/hooks/hooks';
import Space from 'components/UI/Space';
import { ActionMode, DialogOnCloseReason } from 'models/shared';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { SupportCouponCountries } from 'core/enums';
import MultiSelect from 'components/UI/MultiSelect';
import { Coupon } from 'stores/couponsStore';
import { MultiSelectChangeEvent } from 'primereact/multiselect';

const AlphaNumericRegex = /^\w+$/;
const MAX_TRANSACTIONS = '200000';

const eligibleCountries = [
  SupportCouponCountries.AU,
  SupportCouponCountries.ES,
  SupportCouponCountries.FR,
  SupportCouponCountries.IL,
  SupportCouponCountries.IN,
  SupportCouponCountries.MA,
  SupportCouponCountries.MX,
];

const StyledSelect = styled(Select)`
  background-color: light-dark(var(--surface-ground), var(--surface-200));
  color: var(--text-color);
  border: 1px solid var(--surface-border);

  .Mui-disabled {
    color: var(--text-color-secondary);
    -webkit-text-fill-color: var(--text-color-secondary);
    opacity: 0.42;
  }

  ::placeholder {
    color: var(--text-color-secondary);
  }
`;

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
  input {
    box-sizing: unset;
  }
`;

const StyledMultiSelect = styled(MultiSelect)``;

const NoWrappingText = styled(Text)`
  white-space: nowrap;
  min-width: 30%;
  max-width: 30%;
`;

interface CouponEdit
  extends Omit<
    Coupon,
    'params' | 'validFromUTC' | 'validToUTC' | 'growerId' | 'maxTransactions'
  > {
  validFromUTC: Date | null;
  validToUTC: Date | null;
  growerId: string;
  maxTransactions: string | null;
  params: {
    eligibleCountries: string[];
    plotProfileId: number | null;
    userProfileId: number | null;
    maxPlots: number | null;
    isPolygonRequired: boolean;
  };
}

const EMPTY_COUPON: CouponEdit = {
  code: '',
  name: '',
  validFromUTC: null, // note, will convert to UTC timestamp before sending to server
  validToUTC: null, // note, will convert to UTC timestamp before sending to server
  growerId: '',
  maxTransactions: null,
  params: {
    eligibleCountries: [] as string[],
    plotProfileId: null,
    userProfileId: null,
    maxPlots: null,
    isPolygonRequired: false,
  },
};

const convertDateToUtcTimestamp = (date: Date) => {
  const dateAsUtcTimestamp = Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
  );

  return dateAsUtcTimestamp;
};

interface GrowerManagementDialogProps {
  onHide: () => void;
  visible: boolean;
  title: string;
  mode: ActionMode;
}

const CouponManagementDialog: FC<GrowerManagementDialogProps> = observer(
  ({ visible = false, onHide, title, mode }) => {
    const { t } = useTranslation('coupons');
    const { couponsStore, plotsStore } = useStores();
    const [couponEdit, setCouponEdit] = useState(EMPTY_COUPON);
    const [growerNameEdit, setGrowerNameEdit] = useState('name');
    const [fieldHasError, setFieldHasError] = useState<{
      [key: string]: boolean;
    }>({});

    const [initialActionString, setInitialActionString] = useState('');
    useEffect(() => {
      if (initialActionString || growerNameEdit === '') {
        return;
      }

      setInitialActionString(growerNameEdit);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const profileOptions = useMemo(() => {
      return plotsStore.plotProfiles.map((profile) => ({
        id: profile.uid,
        label: profile.name,
      }));
    }, [plotsStore.plotProfiles]);

    const userProfileOptions = useMemo(() => {
      return couponsStore.userProfiles.map((profile) => ({
        id: profile.uid,
        label: profile.profileDescription,
      }));
    }, [couponsStore.userProfiles]);

    const hasError = useMemo(
      () => Object.values(fieldHasError).some((error) => error),
      [fieldHasError],
    );

    const isValidForm = useMemo(() => {
      if (
        couponEdit.code?.trim() &&
        couponEdit.name?.trim() &&
        couponEdit.validFromUTC &&
        couponEdit.validToUTC &&
        couponEdit.growerId?.trim() &&
        couponEdit.maxTransactions &&
        couponEdit.params.eligibleCountries &&
        // couponEdit.params.plotProfileId !== null &&
        // couponEdit.params.maxPlots !== null &&
        !hasError
      ) {
        return true;
      }

      return false;
    }, [couponEdit, hasError]);

    const closeDialog = useCallback(() => {
      setInitialActionString('');
      onHide();
    }, [onHide]);

    const handleCloseDialog = useCallback(
      (event: any, reason: DialogOnCloseReason) => {
        if (reason && reason === DialogOnCloseReason.BackDropClick) {
          return;
        }

        setGrowerNameEdit('');
        closeDialog();
      },
      [closeDialog],
    );

    const handleCloseButton = useCallback(() => {
      setGrowerNameEdit('');
      closeDialog();
    }, [closeDialog]);

    const handleChange = useCallback(
      (
        event: ChangeEvent<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >,
      ) => {
        const { value } = event.target;
        if (event.target.name === 'maxTransactions') {
          if (Number(event.target.value) > Number(MAX_TRANSACTIONS)) {
            setCouponEdit((prev) => ({
              ...prev,
              [event.target.name]: MAX_TRANSACTIONS,
            }));

            return;
          }
        }

        setCouponEdit((prev) => ({
          ...prev,
          [event.target.name]: value,
        }));
      },
      [],
    );

    const handleChangeCode = useCallback(
      (
        event: ChangeEvent<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >,
      ) => {
        const { value } = event.target;
        const isValid = !!AlphaNumericRegex.test(value);
        setCouponEdit((prev) => ({
          ...prev,
          [event.target.name]: value,
        }));

        setFieldHasError((prev) => ({
          ...prev,
          [event.target.name]: !isValid,
        }));
      },
      [],
    );

    const handleChangeSelect = useCallback((event: MultiSelectChangeEvent) => {
      const valueArray = event.value as string[];
      setCouponEdit((prev) => ({
        ...prev,
        params: {
          ...prev.params,
          eligibleCountries: valueArray,
        },
      }));
    }, []);

    const handleParamsChange = useCallback(
      (
        event: ChangeEvent<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >,
      ) => {
        const { name, value, type } = event.target;
        setCouponEdit((prev) => ({
          ...prev,
          params: {
            ...prev.params,
            [name]: value === '' ? null : Number(value),
          },
        }));
      },
      [],
    );

    const handleParamsCheckboxChange = (
      event: React.ChangeEvent<HTMLInputElement>,
      checked: boolean,
    ) => {
      setCouponEdit((prev) => ({
        ...prev,
        params: {
          ...prev.params,
          isPolygonRequired: event.target.checked,
        },
      }));
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleProfileChange = useCallback(
      (event: any, child: React.ReactNode) => {
        if (event?.target?.value) {
          setCouponEdit((prev) => ({
            ...prev,
            params: {
              ...prev.params,
              plotProfileId: Number(event?.target.value),
            },
          }));
        }
      },
      [],
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleUserProfileChange = useCallback(
      (event: any, child: React.ReactNode) => {
        if (event?.target?.value) {
          setCouponEdit((prev) => ({
            ...prev,
            params: {
              ...prev.params,
              userProfileId: Number(event?.target.value),
            },
          }));
        }
      },
      [],
    );

    const handleDateChange = useCallback((event: unknown, id: string) => {
      const date = new Date(event as Date);
      setCouponEdit((prev) => ({
        ...prev,
        [id]: date,
      }));
    }, []);

    console.log('couponEdit', couponEdit);
    const handleSubmitClick = useCallback(async () => {
      if (isValidForm && couponEdit.validFromUTC && couponEdit.validToUTC) {
        // If no countries are selected, sent params: null for the coupon to work everywhere
        const hasNoSelectedCountries =
          couponEdit.params.eligibleCountries.length === 0;

        const paramsObject: { [key: string]: any } = {};
        if (couponEdit.params.eligibleCountries.length) {
          paramsObject.eligibleCountries = couponEdit.params.eligibleCountries;
        }

        if (couponEdit.params.plotProfileId !== null) {
          paramsObject.plotProfileId = couponEdit.params.plotProfileId;
        }

        if (couponEdit.params.userProfileId !== null) {
          paramsObject.userProfileId = couponEdit.params.userProfileId;
        }

        if (couponEdit.params.maxPlots !== null) {
          paramsObject.maxPlots = couponEdit.params.maxPlots;
        }

        paramsObject.isPolygonRequired = couponEdit.params.isPolygonRequired;

        const couponEditCopy: Coupon = {
          ...couponEdit,
          validFromUTC: convertDateToUtcTimestamp(couponEdit.validFromUTC),
          validToUTC: convertDateToUtcTimestamp(couponEdit.validToUTC),
          growerId: Number(couponEdit.growerId),
          maxTransactions: Number(couponEdit.maxTransactions),
          code: couponEdit.code,
          name: couponEdit.name,
          params: Object.keys(paramsObject).length ? paramsObject : null,
        };

        const result = await couponsStore.createCoupon(couponEditCopy);
        if (result) {
          onHide();
        }
      }
    }, [couponEdit, couponsStore, isValidForm, onHide]);

    return (
      <Dialog
        open={visible}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        dialogTitle={title}
        titleWeight="bold"
        disableEscapeKeyDown
      >
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Space height={1} />
            <Grid direction="column" gap="10px">
              <Grid direction="row" gap="10px" alignSelf="start" grow>
                <NoWrappingText>{t('display_name')}:</NoWrappingText>
                <Input
                  required
                  name="code"
                  error={fieldHasError?.code}
                  label={t('display_name')}
                  id="code"
                  value={couponEdit.code}
                  variant="outlined"
                  type="text"
                  fullWidth
                  onChange={handleChangeCode}
                />
              </Grid>
              <Grid direction="row" gap="10px" alignSelf="start" grow>
                <NoWrappingText>{t('name')}:</NoWrappingText>
                <Input
                  required
                  name="name"
                  label={t('name')}
                  id="name"
                  value={couponEdit.name}
                  variant="outlined"
                  type="text"
                  fullWidth
                  onChange={handleChange}
                />
              </Grid>
              <Grid direction="row" gap="10px" alignSelf="start" grow>
                <NoWrappingText>{t('valid_from_utc')}:</NoWrappingText>
                <StyledDatePicker
                  // disabled={isPlantDateFieldDisabled}
                  renderInput={(params: TextFieldProps) => (
                    <TextField {...params} />
                  )}
                  value={couponEdit.validFromUTC}
                  label={t('valid_from_utc')}
                  inputFormat="dd-MMM-yy"
                  mask="__-___-__"
                  onChange={(event: unknown) =>
                    handleDateChange(event, 'validFromUTC')
                  }
                  disableMaskedInput
                  PopperProps={{ placement: 'top-start' }}
                />
              </Grid>
              <Grid direction="row" gap="10px" alignSelf="start" grow>
                <NoWrappingText>{t('valid_to_utc')}:</NoWrappingText>
                <StyledDatePicker
                  // disabled={isPlantDateFieldDisabled}
                  renderInput={(params: TextFieldProps) => (
                    <TextField {...params} />
                  )}
                  value={couponEdit.validToUTC}
                  label={t('valid_to_utc')}
                  inputFormat="dd-MMM-yy"
                  mask="__-___-__"
                  onChange={(event: unknown) =>
                    handleDateChange(event, 'validToUTC')
                  }
                  disableMaskedInput
                  PopperProps={{ placement: 'top-start' }}
                />
              </Grid>
              <Grid direction="row" gap="10px" alignSelf="start" grow>
                <NoWrappingText>{t('grower_id')}:</NoWrappingText>
                <Input
                  required
                  name="growerId"
                  label={t('grower_id')}
                  id="grower_id"
                  value={couponEdit.growerId}
                  variant="outlined"
                  type="number"
                  fullWidth
                  onChange={handleChange}
                />
              </Grid>
              <Grid direction="row" gap="10px" alignSelf="start" grow>
                <NoWrappingText>{t('max_transactions')}:</NoWrappingText>
                <Input
                  required
                  label={t('max_transactions')}
                  id="max_transactions"
                  name="maxTransactions"
                  value={couponEdit.maxTransactions}
                  variant="outlined"
                  type="number"
                  fullWidth
                  onChange={handleChange}
                />
              </Grid>
              <Grid
                direction="row"
                gap="10px"
                alignSelf="start"
                grow
                justify="start"
              >
                <NoWrappingText>{t('eligible_countries')}:</NoWrappingText>
                <StyledMultiSelect
                  id="demo-multiple-name"
                  options={eligibleCountries}
                  value={couponEdit.params.eligibleCountries}
                  onChange={handleChangeSelect}
                  showSelectAll={false}
                />
              </Grid>
              <Grid direction="row" gap="10px" alignSelf="start" grow>
                <NoWrappingText>{t('plot_profile')}:</NoWrappingText>
                <StyledSelect
                  value={couponEdit.params.plotProfileId ?? -1}
                  name="plotProfileId"
                  onChange={handleProfileChange}
                  fullWidth
                  placeholder={t('select_placeholder_plot_profile')}
                >
                  <MenuItem value={-1} disabled>
                    {t('select_placeholder_plot_profile')}
                  </MenuItem>
                  {profileOptions.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.label}
                    </MenuItem>
                  ))}
                </StyledSelect>
              </Grid>
              <Grid direction="row" gap="10px" alignSelf="start" grow>
                <NoWrappingText>{t('user_profile')}:</NoWrappingText>
                <StyledSelect
                  value={couponEdit.params.userProfileId ?? -1}
                  name="userProfileId"
                  onChange={handleUserProfileChange}
                  fullWidth
                  placeholder={t('select_placeholder_user_profile')}
                >
                  <MenuItem value={-1} disabled>
                    {t('select_placeholder_user_profile')}
                  </MenuItem>
                  {userProfileOptions.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.label}
                    </MenuItem>
                  ))}
                </StyledSelect>
              </Grid>
              <Grid direction="row" gap="10px" alignSelf="start" grow>
                <NoWrappingText>{t('max_plots')}:</NoWrappingText>
                <Input
                  name="maxPlots"
                  label={t('max_plots')}
                  id="maxPlots"
                  value={couponEdit.params.maxPlots ?? ''}
                  variant="outlined"
                  type="number"
                  fullWidth
                  onChange={handleParamsChange}
                />
              </Grid>
              <Grid direction="row" gap="10px" alignSelf="start" grow>
                <NoWrappingText>{t('is_polygon_required')}:</NoWrappingText>
                <Checkbox
                  name="isPolygonRequired"
                  id="isPolygonRequired"
                  checked={!!couponEdit.params.isPolygonRequired}
                  onChange={(e) =>
                    handleParamsCheckboxChange(e, e.target.checked)
                  }
                />
              </Grid>
            </Grid>
            <Space height={1} />
          </LocalizationProvider>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button onClick={handleSubmitClick} disabled={!isValidForm}>
            <Text textcolor={theme.color.white} size="sm">
              {t('ok')}
            </Text>
          </Button>
          <Button onClick={handleCloseButton}>
            <Text textcolor={theme.color.white} size="sm">
              {t('cancel')}
            </Text>
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

export default CouponManagementDialog;
